import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useEyeDropper — SSR-safe wrapper around the browser
 * [EyeDropper API](https://developer.mozilla.org/docs/Web/API/EyeDropper).
 *
 *   const { supported, open, picking } = useEyeDropper();
 *   {supported && <button onClick={async () => {
 *     const hex = await open();
 *     if (hex) onChange(hex);
 *   }}>Pick</button>}
 *
 * `supported` is `null` until the first client resolution (matches
 * `useReducedMotion` / `useVoiceInput` semantics) so SSR markup can avoid
 * rendering the eyedropper button and then flashing it away on hydration. After
 * mount it is `true` when `window.EyeDropper` exists, `false` otherwise.
 *
 * `open()` resolves to the picked `sRGBHex` (e.g. `"#0a2540"`), or `null` when
 * the user cancels (Escape) — the cancel rejection is swallowed so consumers
 * never need their own `try/catch`. It is a no-op resolving to `null` when the
 * API is unsupported.
 *
 * Consolidated from the three donor pickers, each of which feature-detected
 * `window.EyeDropper` and hand-wrapped the cancel rejection inline.
 */

/** Shape of the result `EyeDropper.open()` resolves to. */
interface EyeDropperResult {
  /** The picked color as a 6-digit `#rrggbb` sRGB hex. */
  sRGBHex: string;
}

interface EyeDropperInstance {
  open: (options?: { signal?: AbortSignal }) => Promise<EyeDropperResult>;
}

type EyeDropperCtor = new () => EyeDropperInstance;

export interface UseEyeDropperReturn {
  /** `null` during SSR / before first client resolution; `true | false` after. */
  supported: boolean | null;
  /** Whether a pick session is currently open. */
  picking: boolean;
  /**
   * Open the native eyedropper. Resolves to the picked `#rrggbb` hex, or `null`
   * on cancel / when unsupported. Never rejects.
   */
  open: () => Promise<string | null>;
}

const getCtor = (): EyeDropperCtor | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
};

export function useEyeDropper(): UseEyeDropperReturn {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [picking, setPicking] = useState(false);
  // Guard against a state update after unmount when a pick is still open.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    setSupported(typeof getCtor() === "function");
    return () => {
      mounted.current = false;
    };
  }, []);

  const open = useCallback(async (): Promise<string | null> => {
    const Ctor = getCtor();
    if (typeof Ctor !== "function") return null;
    setPicking(true);
    try {
      const result = await new Ctor().open();
      return result.sRGBHex ?? null;
    } catch {
      // User pressed Escape (AbortError) or the call was otherwise rejected —
      // a cancel is not an error to the consumer.
      return null;
    } finally {
      if (mounted.current) setPicking(false);
    }
  }, []);

  return { supported, picking, open };
}
